/* Theme-only enhancements. Purchases always use Shopify's original product form. */
(() => {
  const overlayOpen = () => !!document.querySelector('menu-drawer details[open], header-drawer details[open], pickup-availability-drawer[open], quick-add-modal[open], details-modal details[open], .cart-drawer.active, cart-drawer.active, cart-notification.active, product-modal[open], [data-si-p24-dialog][open], .mobile-facets__disclosure[open]');
  const keyboardOpen = () => /INPUT|TEXTAREA/.test(document.activeElement?.tagName || '') && !!window.visualViewport && window.visualViewport.height < Math.max(window.innerHeight, document.documentElement.clientHeight) * 0.8;

  class ShippingClock extends HTMLElement {
    connectedCallback() {
      this.format = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Warsaw', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
      this.details = this.classList.contains('si-floating-clock') ? this.querySelector('details') : null;
      this.storageKey = 'si-shipping-expanded-' + this.dataset.clockId;
      if (this.details) {
        try { const saved = sessionStorage.getItem(this.storageKey); if (saved !== null) this.details.open = saved === 'true'; } catch (_) {}
        this.onToggle = () => { if (!this.hidden) { try { sessionStorage.setItem(this.storageKey, String(this.details.open)); } catch (_) {} } };
        this.details.addEventListener('toggle', this.onToggle);
      }
      this.tick = () => {
        if (document.hidden) return;
        const parts = Object.fromEntries(this.format.formatToParts(new Date()).map(p => [p.type, p.value]));
        const now = Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
        const cutoff = Number(this.dataset.cutoffHour ?? 22) * 3600 + Number(this.dataset.cutoffMinute || 0) * 60;
        const remaining = cutoff - now;
        // Do not roll over to tomorrow while promising a dispatch today.
        if (remaining <= 0) {
          this.hidden = true;
          this.querySelector('details')?.removeAttribute('open');
          return;
        }
        const source = this.closest('product-info')?.querySelector('button[name="add"]');
        this.hidden = this.hasAttribute('data-product-clock') ? (!source || source.disabled) : overlayOpen();
        const hours = String(Math.floor(remaining / 3600)).padStart(2, '0');
        const minutes = String(Math.floor(remaining % 3600 / 60)).padStart(2, '0');
        const seconds = String(remaining % 60).padStart(2, '0');
        this.querySelectorAll('[data-si-clock-display]').forEach(node => { node.textContent = `${hours}:${minutes}:${seconds}`; });
        // Minute resolution keeps a shipping message calm and avoids a ticking announcement.
        this.querySelectorAll('[data-si-clock]').forEach(node => { const value = remaining < 60 ? 'mniej niż 1 min' : `${hours} godz. ${minutes} min`; if (node.textContent !== value) node.textContent = value; });
      };
      this.tick();
      this.timer = setInterval(this.tick, 1000);
      document.addEventListener('visibilitychange', this.tick);
    }
    disconnectedCallback() { clearInterval(this.timer); document.removeEventListener('visibilitychange', this.tick); this.details?.removeEventListener('toggle', this.onToggle); }
  }

  class StickyBuy extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('product-info');
      this.source = this.product?.querySelector(`#ProductSubmitButton-${CSS.escape(this.dataset.sectionId)}`);
      if (!this.source) return;
      this.button = this.querySelector('[data-si-sticky-button]');
      this.label = this.querySelector('[data-si-sticky-label]');
      this.price = this.querySelector('[data-si-sticky-price]');
      this.mobile = matchMedia('(max-width: 749px)');
      this.sync = () => {
        const disabled = this.source.disabled || this.source.getAttribute('aria-disabled') === 'true';
        this.button.disabled = disabled;
        this.label.textContent = this.source.querySelector('span:not(.loading__spinner)')?.textContent.trim() || this.source.textContent.trim();
        const sourcePrice = this.product.querySelector(`#price-${CSS.escape(this.dataset.sectionId)}`);
        const current = sourcePrice?.querySelector('.price--on-sale .price-item--sale') || sourcePrice?.querySelector('.price-item--regular');
        this.price.textContent = sourcePrice?.classList.contains('visibility-hidden') ? '' : (current?.textContent.trim() || '');
        const error = this.product.querySelector('.product-form__error-message-wrapper');
        if (this.pending && error && !error.hidden && error.textContent.trim()) {
          this.pending = false;
          error.tabIndex = -1;
          error.scrollIntoView({ block: 'center' });
          error.focus({ preventScroll: true });
        }
        if (!this.source.classList.contains('loading') && this.source.getAttribute('aria-disabled') !== 'true') this.pending = false;
        this.schedule();
      };
      this.update = () => {
        this.frame = null;
        // Show after the original purchase button has passed above the viewport.
        const past = this.source.getBoundingClientRect().bottom < 0;
        this.hidden = !(this.mobile.matches && past && !overlayOpen() && !keyboardOpen());
        if (document.body.classList.contains('si-sticky-buy-visible') === this.hidden) document.body.classList.toggle('si-sticky-buy-visible', !this.hidden);
      };
      this.schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(this.update); };
      this.onClick = () => {
        if (this.source.disabled || this.source.getAttribute('aria-disabled') === 'true') return;
        const quantity = this.product.querySelector('input[name="quantity"]');
        if (quantity && !quantity.checkValidity()) { quantity.scrollIntoView({ block: 'center' }); quantity.reportValidity(); return; }
        this.pending = true;
        this.source.click();
      };
      this.button.addEventListener('click', this.onClick);
      this.observer = new MutationObserver(this.sync);
      this.observer.observe(this.product.querySelector('product-form'), { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
      const price = this.product.querySelector(`#price-${CSS.escape(this.dataset.sectionId)}`);
      if (price) this.observer.observe(price, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class'] });
      window.addEventListener('scroll', this.schedule, { passive: true });
      window.addEventListener('resize', this.schedule, { passive: true });
      document.addEventListener('toggle', this.schedule, true);
      document.addEventListener('click', this.schedule);
      document.addEventListener('focusin', this.schedule);
      document.addEventListener('focusout', this.schedule);
      this.overlayObserver = new MutationObserver(this.schedule);
      this.overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      ['cart-drawer', 'cart-notification', '.cart-notification', 'product-modal', 'quick-add-modal', 'pickup-availability-drawer'].forEach(tag => document.querySelectorAll(tag).forEach(el => this.overlayObserver.observe(el, { attributes: true, attributeFilter: ['class', 'open'] })));
      this.sync();
    }
    disconnectedCallback() {
      this.observer?.disconnect(); this.overlayObserver?.disconnect(); cancelAnimationFrame(this.frame);
      if (this.schedule) {
        window.removeEventListener('scroll', this.schedule); window.removeEventListener('resize', this.schedule);
        document.removeEventListener('toggle', this.schedule, true);
        ['click', 'focusin', 'focusout'].forEach(event => document.removeEventListener(event, this.schedule));
      }
      document.body.classList.remove('si-sticky-buy-visible');
    }
  }
  if (!customElements.get('si-shipping-clock')) customElements.define('si-shipping-clock', ShippingClock);
  if (!customElements.get('si-sticky-buy')) customElements.define('si-sticky-buy', StickyBuy);

  // Delegation survives Shopify's AJAX cart updates and theme editor replacements.
  const updateCart = () => {
    const footer = document.getElementById('main-cart-footer');
    if (!footer) return;
    const accepted = !!document.getElementById('terms-checkbox')?.checked;
    const delivery = footer.querySelector('si-delivery-choice');
    const busy = !!delivery?.hasAttribute('data-pending');
    const ready = !delivery || (delivery.dataset.ready === 'true' && !delivery.failed && !busy && delivery.canCheckout);
    footer.querySelectorAll('.additional-checkout-buttons').forEach(el => { el.hidden = !(accepted && ready); el.inert = !(accepted && ready); });
    const checkout = footer.querySelector('#checkout');
    if (checkout) {
      const blocked = footer.classList.contains('is-empty') || !ready;
      checkout.disabled = blocked;
      const control = footer.querySelector('[data-si-checkout-control]');
      const hint = footer.querySelector('[data-si-checkout-hint]');
      const tooltip = footer.querySelector('[data-si-checkout-tooltip]');
      const message = delivery?.failed ? 'Nie udało się usunąć poprzedniego punktu. Spróbuj ponownie w sekcji dostawy.'
        : busy ? 'Poczekaj na potwierdzenie danych dostawy…'
        : !ready ? 'Wybierz punkt odbioru albo zmień sposób dostawy na kuriera.' : '';
      if (hint) { hint.hidden = !message; if (hint.textContent !== message) hint.textContent = message; }
      if (tooltip && tooltip.textContent !== message) tooltip.textContent = message;
      if (control) {
        control.toggleAttribute('data-blocked', blocked);
        if (message) { control.tabIndex = 0; control.setAttribute('role', 'group'); control.setAttribute('aria-label', 'Finalizacja zamówienia'); control.setAttribute('aria-describedby', hint.id); checkout.setAttribute('aria-describedby', hint.id); }
        else { control.removeAttribute('tabindex'); control.removeAttribute('role'); control.removeAttribute('aria-label'); control.removeAttribute('aria-describedby'); checkout.removeAttribute('aria-describedby'); }
      }
    }
    if (accepted) { const error = document.getElementById('terms-info'); if (error) error.hidden = true; document.getElementById('terms-checkbox')?.removeAttribute('aria-invalid'); }
  };
  document.addEventListener('change', event => { if (event.target.id === 'terms-checkbox') updateCart(); });
  document.addEventListener('si:delivery-state', updateCart);
  document.addEventListener('invalid', event => {
    if (event.target.id !== 'terms-checkbox') return;
    const error = document.getElementById('terms-info'); if (error) error.hidden = false;
    event.target.setAttribute('aria-invalid', 'true');
  }, true);
  document.addEventListener('submit', event => {
    if (event.target.id !== 'cart' || event.submitter?.name !== 'checkout') return;
    const checkbox = document.getElementById('terms-checkbox');
    if (checkbox && !checkbox.checked) { event.preventDefault(); checkbox.reportValidity(); checkbox.focus(); }
  }, true);
  let shippingObserver;
  let shippingFrame;
  function placeCartShipping() {
    shippingFrame = null;
    const slot = document.querySelector('[data-si-cart-shipping]');
    const bar = document.getElementById('shipping-container-cart_page_only');
    if (slot && bar && !slot.contains(bar)) slot.appendChild(bar);
    if (bar && slot?.contains(bar)) bar.classList.add('si-shipping-placed');
  }
  function initCart() {
    placeCartShipping();
    if (!shippingObserver && document.getElementById('main-cart-footer')) {
      shippingObserver = new MutationObserver(() => { if (!shippingFrame) shippingFrame = requestAnimationFrame(placeCartShipping); });
      shippingObserver.observe(document.getElementById('MainContent') || document.body, { subtree: true, childList: true });
    }
    updateCart();
    const footer = document.getElementById('main-cart-footer');
    if (!footer || footer.dataset.siObserved) return;
    footer.dataset.siObserved = 'true';
    const observer = new MutationObserver(updateCart);
    observer.observe(footer, { childList: true, subtree: true });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, updateCart);
  }
  const labelReviewWidgets = () => document.querySelectorAll('.jdgm-verified-count-badget').forEach(el => {
    if (!el.textContent.trim() && !el.hasAttribute('aria-label')) el.setAttribute('aria-label', 'Zobacz opinie klientów');
  });
  labelReviewWidgets();
  document.addEventListener('shopify:section:load', labelReviewWidgets);
  initCart();
  document.addEventListener('shopify:section:load', initCart);
})();
